'use client';

import React, { useMemo, useState } from 'react';
import { Alert, Typography, message } from 'antd';
import { FiClock, FiEye } from 'react-icons/fi';

import ThemedButton from '@/components/common/buttons/Button';
import DocumentPreviewDrawer from '@/components/drawers/DocumentPreviewDrawer';
import { DocumentDetails } from '@/redux/docsVerification/types';

import { fmtDate, fmtDateTime, isLikelyUsableFileUrl } from '../utils';
import InfoBlock from './InfoBlock';
import ExpandableItemsBlock from './ExpandableItemsBlock';
import SecurityCodeCard from './SecurityCodeCard';
import StatusStamp from './StatusStamp';

const { Title, Text } = Typography;

interface DocumentVerificationCardProps {
  document: DocumentDetails;
  isExpired: boolean;
}

const DocumentVerificationCard = ({
  document,
  isExpired,
}: DocumentVerificationCardProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  const isValid = !isExpired;

  const hasPreviewFile = useMemo(
    () => isLikelyUsableFileUrl(document.file_url),
    [document.file_url],
  );

  const drawerKey = useMemo(
    () =>
      `${document.id}-${document.file_url ?? 'no-file'}-${document.created_at}`,
    [document.id, document.file_url, document.created_at],
  );

  const handleOpenPreview = () => {
    if (!hasPreviewFile) {
      message.error('No file available for preview');
      return;
    }
    setPreviewOpen(true);
  };

  return (
    <>
      <div
        style={{
          background: '#ffffff',
          border: '2px solid var(--color-primary)',
          boxShadow: '0 2px 16px rgba(139,115,85,0.12)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {(
          ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const
        ).map((corner) => {
          const isTop = corner.includes('top');
          const isLeft = corner.includes('left');
          return (
            <div
              key={corner}
              style={{
                position: 'absolute',
                top: isTop ? 10 : undefined,
                bottom: !isTop ? 10 : undefined,
                left: isLeft ? 10 : undefined,
                right: !isLeft ? 10 : undefined,
                width: 36,
                height: 36,
                borderTop: isTop ? '2px solid var(--color-primary)' : undefined,
                borderBottom: !isTop
                  ? '2px solid var(--color-primary)'
                  : undefined,
                borderLeft: isLeft
                  ? '2px solid var(--color-primary)'
                  : undefined,
                borderRight: !isLeft
                  ? '2px solid var(--color-primary)'
                  : undefined,
                pointerEvents: 'none',
              }}
            />
          );
        })}

        <div
          style={{
            borderBottom: '1px solid var(--color-primary)',
            padding: '24px 36px 20px',
            background: '#fafaf7',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              marginBottom: 18,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
            }}
          >
            Official Documents Verification
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, minWidth: 200 }}>
              <Title level={2} className="mb-1! text-[22px]! sm:text-[26px]!">
                Document Verification
              </Title>
              <Text
                type="secondary"
                className="text-sm"
                style={{ fontStyle: 'italic' }}
              >
                This certificate confirms the authenticity and validity status
                of the referenced document.
              </Text>
            </div>

            <StatusStamp status={isValid ? 'valid' : 'expired'} />
          </div>
        </div>

        <div style={{ padding: '28px 36px', background: '#ffffff' }}>
          <div
            style={{
              border: `1px solid ${isValid ? '#b7d4b7' : '#d4a87a'}`,
              background: isValid ? '#f6faf6' : '#fdf8f2',
              padding: '14px 20px',
              marginBottom: 28,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              borderRadius: 0,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: isValid ? '#1a4a1a' : 'var(--primary-color)',
                  marginBottom: 3,
                }}
              >
                {isValid ? 'Status: Valid' : 'Status: Expired'}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: isValid ? '#2d5a2d' : '#7a4020',
                  fontStyle: 'italic',
                }}
              >
                {isValid
                  ? 'This document exists in the system and is currently valid for verification purposes.'
                  : 'This document exists in the system, but its validity period has lapsed. Confirm whether a renewal or replacement has been issued.'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <div className="dvc-row-3">
              <InfoBlock
                label="Document Number"
                value={document.number}
                copyValue={document.number}
              />
              <InfoBlock label="Document Type" value={document.type} />
              <InfoBlock
                label="Record Created At"
                value={fmtDateTime(document.created_at)}
              />
            </div>

            <div className="dvc-row-1-2">
              <InfoBlock
                label="Application Type"
                value={document.item?.name || '—'}
              />
              <InfoBlock
                label="Application Notes"
                value={document.notes || '—'}
                copyValue={document.notes || null}
              />
            </div>

            <div className="dvc-row-3">
              <div style={{ overflow: 'visible', minWidth: 0 }}>
                <ExpandableItemsBlock
                  label="Application Items"
                  items={document.specific_items}
                />
              </div>
              <InfoBlock
                label="Issue Date"
                value={fmtDate(document.issue_date)}
              />
              <InfoBlock
                label="Expiry Date"
                value={
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    {fmtDate(document.expiry_date)}
                    {isExpired && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          fontSize: 11,
                          color: 'var(--color-primary)',
                          border: '1px solid var(--primary-color)',
                          padding: '1px 7px',
                          borderRadius: 0,
                          background: '#fff8f0',
                        }}
                      >
                        <FiClock size={10} />
                        Expired
                      </span>
                    )}
                  </span>
                }
              />
            </div>
          </div>

          <div style={{ margin: '32px 0 16px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 16,
                paddingBottom: 10,
                borderBottom: '1px solid var(--color-primary)',
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--primary-color)',
                }}
              >
                Security Code Validation
              </span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 16,
              }}
            >
              <SecurityCodeCard
                title="Security Code Horizontal"
                value={document.h_security_no}
              />
              <SecurityCodeCard
                title="Security Code Vertical"
                value={document.v_security_no}
              />
            </div>
          </div>

          <div
            style={{
              border: '1px solid var(--color-primary)',
              background: 'rgba(59, 130, 246, 0.06)',
              padding: '18px 22px',
              marginBottom: 28,
              marginTop: 20,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                marginBottom: 10,
              }}
            >
              Verification Guidance
            </div>

            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                listStyle: 'disc',
              }}
            >
              {[
                'Confirm that the document number matches the presented document.',
                'Check that the document type, application type, and application items are correct.',
                'Review the application notes for the exact vehicle or transaction context.',
                'Ensure the document has not expired where an expiry date is applicable.',
                'Compare both security codes exactly as displayed above against the original document. Matching codes confirm authenticity.',
              ].map((item, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: 13,
                    color: 'rgba(0, 0, 0, 0.78)',
                    fontStyle: 'italic',
                  }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>

        </div>

        <div
          style={{
            borderTop: '1px solid var(--color-primary)',
            background: '#fafaf7',
            padding: '10px 36px',
            textAlign: 'center',
          }}
        >
          <Text
            type="secondary"
            style={{ fontSize: 11, letterSpacing: '0.1em' }}
          >
            This is an electronically generated verification certificate. No
            signature is required.
          </Text>
        </div>
      </div>

      {hasPreviewFile && (
        <DocumentPreviewDrawer
          key={drawerKey}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          fileUrl={document.file_url ?? null}
          number={document.number ?? null}
          createdAt={document.created_at ?? null}
          documentType="authorization"
          document={document}
        />
      )}

      <style jsx>{`
        /* Row 1 & 3: equal three columns */
        .dvc-row-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          column-gap: 24px;
          overflow: visible;
        }

        /* Row 2: one-third / two-thirds */
        .dvc-row-1-2 {
          display: grid;
          grid-template-columns: 1fr 2fr;
          column-gap: 24px;
          overflow: visible;
        }

        /* Ensure list bullets inside grid cells are never clipped */
        .dvc-row-3 ul,
        .dvc-row-1-2 ul {
          padding-left: 16px;
          overflow: visible;
        }

        /* Collapse to single column on tablet and below */
        @media (max-width: 992px) {
          .dvc-row-3,
          .dvc-row-1-2 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default DocumentVerificationCard;
